import * as React from "react"
import type { EntityTransaction, Transaction } from "@digits-graphql/frontend/graphql-bearer"
import { vTableCellRender } from "@digits-shared/components/UI/VirtualizedTable/VTableCell"
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table"
import {
  AmountCell,
  LoadingAmountCell,
} from "src/frontend/components/Shared/Transactions/TableCells/AmountCell"
import {
  CategoryCell,
  LoadingCategoryCell,
} from "src/frontend/components/Shared/Transactions/TableCells/CategoryCell"
import {
  CheckboxCell,
  CheckboxHeaderCell,
} from "src/frontend/components/Shared/Transactions/TableCells/CheckboxCell"
import {
  DateCell,
  LoadingDateCell,
} from "src/frontend/components/Shared/Transactions/TableCells/DateCell"
import {
  LoadingPartyCell,
  PartyCell,
} from "src/frontend/components/Shared/Transactions/TableCells/PartyCell"
import { ReferenceNumberCell } from "src/frontend/components/Shared/Transactions/TableCells/ReferenceNumberCell"
import {
  LoadingSourceCell,
  SourceCell,
} from "src/frontend/components/Shared/Transactions/TableCells/SourceCell"
import { SplitCategoryCell } from "src/frontend/components/Shared/Transactions/TableCells/SplitCategoryCell"
import {
  type ColumnOptions,
  isTransaction,
} from "src/frontend/components/Shared/Transactions/types"
import { useTransactionsUpdates } from "src/frontend/components/Shared/Transactions/Updates/TransactionsUpdatesContext"

export function useTableColumns({
  partyIconOnly = false,
  includeSources = true,
  includeRelatedCategory = true,
  enableSorting = false,
}: ColumnOptions) {
  const { editable, editing, allowBulkUpdate } = useTransactionsUpdates()

  return React.useMemo(() => {
    const columnHelper = createColumnHelper<Transaction | EntityTransaction>()
    const columns: ColumnDef<Transaction | EntityTransaction>[] = []

    if (editable && allowBulkUpdate) {
      columns.push(
        columnHelper.display({
          id: "checkbox",
          header: ({ table }) => (
            <CheckboxHeaderCell
              rowSelection={table.getState().rowSelection}
              toggleRowSelection={table.getToggleAllRowsSelectedHandler()}
            />
          ),
          size: editing ? 48 : 0,
          minSize: 0,
          cell: vTableCellRender({
            LoaderCell: LoadingDateCell,
            Cell: ({ data }) => {
              if (isTransaction(data)) {
                return <CheckboxCell transaction={data} />
              }
              return null
            },
          }),
        })
      )
    }

    columns.push(
      columnHelper.accessor("timestamp", {
        header: "Date",
        size: 70,
        enableSorting,
        cell: vTableCellRender({
          LoaderCell: LoadingDateCell,
          Cell: ({ data }) => <DateCell transaction={data} />,
        }),
      })
    )

    columns.push(
      columnHelper.accessor("party", {
        header: "Party",
        enableSorting,
        size: partyIconOnly ? 70 : undefined,
        cell: vTableCellRender({
          LoaderCell: LoadingPartyCell,
          Cell: ({ data }) => <PartyCell transaction={data} iconOnly={partyIconOnly} />,
        }),
      })
    )

    if (includeSources) {
      columns.push(
        columnHelper.display({
          id: "source",
          header: "Source",
          minSize: 66,
          cell: vTableCellRender({
            LoaderCell: LoadingSourceCell,
            Cell: ({ data }) => {
              if (isTransaction(data)) {
                return <SourceCell transaction={data} />
              }
              return null
            },
          }),
        })
      )
    }

    if (includeRelatedCategory) {
      columns.push(
        columnHelper.accessor("splitCategory", {
          id: "relatedCategory",
          header: "Related Category",
          cell: vTableCellRender({
            LoaderCell: LoadingCategoryCell,
            Cell: ({ data }) => <SplitCategoryCell transaction={data} />,
          }),
        })
      )
    }

    columns.push(
      columnHelper.accessor("displayCategory", {
        header: "Source Category",
        cell: vTableCellRender({
          LoaderCell: LoadingCategoryCell,
          Cell: ({ data }) => <CategoryCell transaction={data} />,
        }),
      })
    )

    columns.push(
      columnHelper.accessor("referenceNumber", {
        header: "Reference Number",
        size: 140,
        cell: vTableCellRender({
          LoaderCell: LoadingAmountCell,
          Cell: ({ data }) => {
            if (isTransaction(data)) {
              return <ReferenceNumberCell transaction={data} />
            }
            return null
          },
        }),
      })
    )

    columns.push(
      columnHelper.accessor("moneyFlow", {
        header: "Amount",
        size: 120,
        enableSorting,
        cell: vTableCellRender({
          LoaderCell: LoadingAmountCell,
          Cell: ({ data }) => <AmountCell transaction={data} />,
        }),
      })
    )

    return columns
  }, [
    editable,
    allowBulkUpdate,
    enableSorting,
    partyIconOnly,
    includeSources,
    includeRelatedCategory,
    editing,
  ])
}
